import { FormControl, Grid, Select, MenuItem, InputLabel, Box, Checkbox, IconButton, InputAdornment, ListSubheader, TextField, Typography, SelectChangeEvent } from "@mui/material";

import React, { memo, useCallback, useEffect, useRef, useState } from "react";

import {
	flexCenterProps,
	paddingX1Props,
	requiredLabelStyle,
} from "@styles/global-css-class";
import { FieldError } from "@components/FieldError";
import { IMultipleSelectFormControlProperty, ListItem, SELECT_ALL_VALUE } from "@definitions/index";
import { colors } from "@components/ThemeRegistry/theme";
import DiffInheritanceIndicator from "@components/DiffInheritanceIndicator";
import { StyledFormLabel } from "@components/Common";

function NewMultipleSelectFormControl({
	childKey,
	label,
	name,
	value,
	defaultValue,
	onChange,
	isRequired,
	isDisabled,
	isHidden,
	listValues,
	placeHolder,
	formState,
	zIndex,
	xsFormControl = 12,
	mdFormControl = 12,
	xsLabel,
	xsInput,
	textAlign,
	textErrorAlign,
	itemSx,
	textSx,
	errorName,
	isExpanded,
	isHideLabel = true,
	showIndicator,
	isIndicatorTakeSpace,
	isShowRequiredAsterisk = true,
	showAllSelection = true,
	...props
}: Readonly<IMultipleSelectFormControlProperty>) {
	const [valueFilter, setValueFilter] = useState("");
	const [listItems, setListItems] = useState<ListItem[]>(listValues);
	const [selectedValues, setSelectedValues] = useState<string[]>([]);
	const inputRef = useRef<HTMLInputElement>(null);

	const getFilteredOptions = useCallback((filterText: string) => {
		if (!filterText) {
			return listValues || [];
		}

		filterText = filterText.toLowerCase();
		return listValues.filter((option: any) => {
			return option.value?.toLowerCase().includes(filterText)
				|| option.name?.toLowerCase().includes(filterText);
		});
	}, [listValues])

	const handleFilterChange = useCallback((event: any) => {
		setValueFilter(event.target.value);
	}, []);

	const handleClearFilter = useCallback(() => {
		setValueFilter('');
	}, []);

	useEffect(() => {
		if (isExpanded && inputRef.current) {
			inputRef.current?.focus();
		}
	}, [isExpanded, inputRef.current, listItems]);

	useEffect(() => {
		if (defaultValue?.length > 0) {
			setSelectedValues(defaultValue);
			return;
		}

		const splitValues = typeof value === "string" && value?.split(",").length > 0 ? value.split(",") : value;
		setSelectedValues(splitValues);
	}, [defaultValue, value]);


	useEffect(() => {
		if (listValues) {
			setListItems(getFilteredOptions(valueFilter));
		}
	}, [listValues, valueFilter])

	const renderSelectValues = useCallback((selectedValues: string[]) => {
		if (!selectedValues || selectedValues.length == 0) {
			return '';
		}
		const styles = {
			textAlign: "left",
			textOverflow: "ellipsis",
			overflow: "hidden",
			whiteSpace: "nowrap",
		};

		if (showAllSelection) {
			const hasAllValue = selectedValues.includes(SELECT_ALL_VALUE);
			//Remove select all value from selected values
			const itemCount = hasAllValue ? selectedValues.length - 1 : selectedValues.length;
			if (itemCount == 0) {
				return '';
			}

			if (itemCount === listValues?.length) {
				return <Box sx={styles}>All</Box>;
			}
		}
		const displayNames: string[] = selectedValues
			.map(value => listValues.find(item => item.value === (defaultValue ?? value))?.name ?? value ?? "");
		return <Box sx={styles}>{displayNames?.join(", ")}</Box>;
	}, [listValues, showAllSelection, defaultValue, value]);

	const handleValueChange = useCallback((event: SelectChangeEvent<string | string[]>) => {
		const value = event.target.value;
		if (value[value.length - 1] === SELECT_ALL_VALUE) {
			setSelectedValues(selectedValues?.length === listItems.length ? [] : listItems.map((v) => v.value.toString()));
			onChange(selectedValues?.length === listItems.length ? [] : listItems.map((v) => v.value.toString()));
			return;
		}
		const splitValues = typeof value === "string" ? value.split(",") : value;
		setSelectedValues(splitValues);
		onChange(splitValues);
	}, [listItems, selectedValues]);

	return <Grid container item xs={xsFormControl} md={mdFormControl} sx={itemSx ?? {
		...paddingX1Props,
		alignContent: 'flex-start'
	}}>
		<FormControl
			key={`${childKey}-form-control`}
			fullWidth margin='normal' {...props}
			error={
				formState?.errors[errorName ?? name] !==
				undefined
			}
		>
			<Grid container sx={flexCenterProps}>
				{!isHideLabel && <Grid xs={xsLabel} sx={itemSx ?? { pr: 1 }}>
					<StyledFormLabel
						aria-describedby={name}
						hidden={isHidden}
						error={formState?.errors[errorName ?? name] !== undefined}
						sx={textSx ?? {
							...requiredLabelStyle,
							justifyContent: textAlign ?? "right",
						}}>
						{label}
						{isRequired && isShowRequiredAsterisk && ' *'}
					</StyledFormLabel>
				</Grid>}
				<Grid container flexWrap={'nowrap'} xs={xsInput ?? (!xsLabel || xsLabel === 12 ? 12 : 12 - xsLabel)} sx={{ position: "relative" }}>
					<Grid item xs={isIndicatorTakeSpace ? 11 : 12} sx={{ position: "relative" }}>
						{isHideLabel && <InputLabel
							key={`${childKey}-form-control-input-label`}
							id={name}
							sx={textSx ?? {}}
							error={formState?.errors[errorName ?? name] !== undefined}
						>
							{label}
							{isRequired && isShowRequiredAsterisk && ' *'}
						</InputLabel>}
						<Select
							key={`${childKey}-form-control-select`}
							labelId={`${name}-label`}
							label={isHideLabel ? label : undefined}
							multiple
							value={selectedValues ?? defaultValue ?? []}
							disabled={isDisabled}
							inputRef={inputRef}
							onChange={handleValueChange}
							renderValue={renderSelectValues}
							onClose={() => handleClearFilter}
							sx={{
								width: "100%"
							}}
							MenuProps={{
								autoFocus: false,
								style: {
									zIndex: zIndex ?? 0 + 2,
								},
								PaperProps: {
									sx: {
										maxHeight: "20vh",
										width: 200,
										"@media screen and (max-width: 1366px) and (max-height: 768px)":
										{
											maxHeight:
												"15vh",
										},
									},
								},
							}}>
							<ListSubheader sx={{ height: '40px' }}>
								<TextField
									value={valueFilter}
									size="small"
									// Autofocus on textfield
									autoFocus
									placeholder="Type to search..."
									fullWidth
									inputProps={{
										endAdornment: (
											<InputAdornment position="end">
												<IconButton
													aria-label="clear search"
													onClick={handleClearFilter}
												>
													x
												</IconButton>
											</InputAdornment>
										)
									}}
									//upgrade material 6
									// slotProps={{
									// 	input: {
									// 		endAdornment: (
									// 			<InputAdornment position="end">
									// 				<IconButton
									// 					aria-label="clear search"
									// 					onClick={handleClearFilter}
									// 				>
									// 					x
									// 				</IconButton>
									// 			</InputAdornment>
									// 		)
									// 	}
									// }}
									onChange={handleFilterChange}
									onKeyDown={(e) => {
										if (e.key !== "Escape") {
											// Prevents auto selecting item while typing (default Select behavior)
											e.stopPropagation();
										}
									}}
								/>
							</ListSubheader>

							{showAllSelection && <MenuItem key="select-all" value={SELECT_ALL_VALUE} style={{ borderBottom: "2px solid #e0e0e0", }}>
								<Checkbox
									sx={{
										color: colors.slate,
										"&.Mui-checked":
										{
											color: colors.slate,
										},
									}}
									checked={listItems.length === selectedValues?.length}
								/>
								Select All
							</MenuItem>}

							{listItems?.map((v, index) => (
								<MenuItem
									key={`${v.value}-${index}-menu-item`}
									value={
										`${v.value}`
									}>
									<Checkbox
										key={`${v.value}-${index}-chk`}
										sx={{
											color: colors.slate,
											"&.Mui-checked":
											{
												color: colors.slate,
											},
										}}
										checked={
											selectedValues?.indexOf(
												`${v.value}`
											) > -1
										}
									/>
									<Box
										key={`${v.value}-${index}-name`}
										sx={{
											overflow:
												"hidden",
										}}>
										<Typography
											sx={{
												wordWrap:
													"break-word",
												whiteSpace:
													"normal",
												overflowWrap:
													"break-word",
											}}>
											{
												v.name
											}
										</Typography>
									</Box>
								</MenuItem>
							))}
						</Select>
					</Grid>
					<Grid item xs={isIndicatorTakeSpace ? 1 : 0} sx={{ ...flexCenterProps }}>
						{showIndicator !== undefined && showIndicator() == true && <DiffInheritanceIndicator />}
					</Grid>

				</Grid>

			</Grid>
			{formState && <FieldError key={`${childKey}-form-control-error`} formState={formState} name={errorName ?? name} textAlign={textErrorAlign} />}
		</FormControl>
	</Grid >;
}

export default memo(NewMultipleSelectFormControl);
